
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";

  import GET_SOFT_SKILLS from "@/apollo/autocomplete-queries/GetSoftSkills.gql";

  import DropdownFilter from "@/components/filters/DropdownFilterNew.vue";

  import filterMultiselectItems from "@/helpers/multiselect/filterMultiselectItems";
  import SoftSkillMultiselectOption from "@/entities/SoftSkillMultiselectOption";

  @Component({
    components: {
      DropdownFilter,
    },
    apollo: {
      softSkills: {
        query: GET_SOFT_SKILLS,
        variables() {
          return this.softSkillAutocompleteFilter;
        },
        update(data) {
          return data.soft_skill;
        },
        skip() {
          return !this.softSkillSearchString;
        },
      },
    },
  })
  export default class SoftSkillsMultiselect extends Vue {
    @Prop({ type: Array, required: true }) readonly value!: string[];
    @Prop({ type: Boolean, default: true }) readonly withBorder!: boolean;
    @Prop({ type: Number, default: 5 }) readonly limit!: number;
    @Prop({ type: String, default: "" }) readonly pillsColor: string;
    @Prop({ type: Number, default: undefined }) readonly max?: number;
     @Prop({ type: String, default: "" }) readonly getcontainerStyle?: string;
     @Prop({ type: String, default: undefined }) readonly getParentStyle?: string;
     @Prop({ type: Object, default: () => ({}) }) readonly condition!: Object;
     @Prop({ type: String, default: undefined }) readonly currentUserId?: string;
    @Prop({ type: String, default: undefined }) readonly companyId?: string;
    @Prop({ type: Boolean, default: false }) readonly enableSoftSkillsAdding!: boolean;
    @Prop({ type: Boolean, default: false }) readonly withCustomSoftSkills!: boolean;
    @Prop({ type: Array, required: true }) readonly previouslyChosenSoftSkillsIds!: number[];
    @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
    @Prop({ type: String, default: "Enter Tag" }) readonly placeholder: string;

    softSkills: SoftSkillMultiselectOption[] = [];
    softSkillSearchString: string = "";
    chosenSoftSkills: SoftSkillMultiselectOption[] = [];
    newSoftSkillsNames: string[] = [];

    get softSkillsOptions(): SoftSkillMultiselectOption[] {
      if (!this.withCustomSoftSkills) {
        return this.softSkills;
      }

      const chosenSoftSkillsNames = this.chosenSoftSkills.map(({ name }) => name);

      const searchSoftSkillAlreadyChosen = [
        ...chosenSoftSkillsNames,
        ...this.newSoftSkillsNames,
      ].find(softSkillName => softSkillName?.toLowerCase() === this.softSkillSearchString?.toLowerCase());

      if (searchSoftSkillAlreadyChosen || this.softSkillFoundInList) {
        return this.softSkills;
      }

      return [
        {
          id: 0 - Date.now(),
          name: this.softSkillSearchString,
          company_id: null,
          user_id: null,
        },
        ...this.softSkills,
      ];
    }

   get computedCustomStyles(): string {
    const styles = [this.getParentStyle, this.getcontainerStyle].filter(style => !!style);
    return styles.join(' ');
  }
  
    get softSkillAutocompleteFilter(): any {
      const conditions: any[] = [];

      if (this.softSkillSearchString) {
        conditions.push({
          name: {
            _ilike: `%${this.softSkillSearchString}%`,
          },
        });
      }

      if (this.value.length) {
        conditions.push({
          id: {
            _nin: this.value.filter(id => Number(id) >= 0),
          },
        });
      }

      // Add company filtering: show soft skills that either belong to the user's company or are global (company_id is null)
      if (this.companyId) {
        conditions.push({
          _or: [
            { company_id: { _eq: this.companyId } },
            { company_id: { _is_null: true } }
          ]
        });
      } else {
        // If no companyId, only show global soft skills
        conditions.push({
          company_id: { _is_null: true }
        });
      }

      if (conditions.length === 0) {
        return {
          where: this.condition,
          limit: this.limit,
        };
      }

      return {
        where: { _and: [...conditions, this.condition] },
        limit: this.limit,
      };
    }

    @Watch("value", { immediate: true })
    async onSoftSkillsIdsChanged(): Promise<void> {
      const softSkillsIds = this.value;

      if (softSkillsIds.length === 0) {
        this.chosenSoftSkills = [];
        return;
      }

      const {
        newItemsIds: newSoftSkillIds,
        previouslyChosenItems: previouslyChosenSoftSkills,
      } = filterMultiselectItems(softSkillsIds, this.chosenSoftSkills);

      if (newSoftSkillIds.length === 0) {
        return;
      }

      const rawData = await this.$apollo.query({
        query: GET_SOFT_SKILLS,
        variables: {
          where: {
            id: {
              _in: newSoftSkillIds,
            },
          },
        },
      })

      const newChosenSoftSkills = rawData.data.soft_skill;
      this.chosenSoftSkills = [...previouslyChosenSoftSkills, ...newChosenSoftSkills];
    }

    get softSkillFoundInList(): boolean {
      return this.softSkills.some(({name}) => name === this.softSkillSearchString)
    }

    updateSearch(searchValue: string): void {
      this.softSkillSearchString = searchValue;
      this.$emit("search", searchValue);
    }

    isNewSoftSkill(softSkill: SoftSkillMultiselectOption): boolean {
      if (softSkill.id >= 0) {
        return false;
      }

      return !this.softSkillFoundInList || this.newSoftSkillsNames.some(softSkillName => softSkillName === softSkill.name);
    }

    handleInput(chosenSoftSkills: SoftSkillMultiselectOption[]): void {
      this.chosenSoftSkills = chosenSoftSkills;
      
      this.$emit("inputSoftSkills", chosenSoftSkills);
      if (!this.withCustomSoftSkills) {
        const chosenSoftSkillsIds = chosenSoftSkills.map(option => Number(option.id));

        this.$emit("input", chosenSoftSkillsIds);
        return;
      }

      const chosenSoftSkillsIds = chosenSoftSkills
        .filter(chosenSoftSkill => chosenSoftSkill.id >= 0)
        .map(option => Number(option.id));

      this.$emit("input", chosenSoftSkillsIds, chosenSoftSkills);
    }
  }
